import { Component, inject, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatInput } from '@angular/material/input';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatError, MatLabel } from '@angular/material/form-field';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Moment } from 'moment';
import { merge, Subject, takeUntil } from 'rxjs';
import { StormService } from '../../../../../data/storms/storm.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { DateTools } from '../../../../../shared/tools/DateTools';

const MAX_RANGE_HOURS = 3*24;

// Group-level validator: 'to' must be after 'from', and range must not exceed MAX_RANGE_DAYS
function dateRangeValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const from: Moment | null = group.get('from')?.value;
    const to: Moment | null = group.get('to')?.value;
    const fromTime: string | null = group.get('fromTime')?.value;
    const toTime: string | null = group.get('toTime')?.value;

    if (!from || !to) {
      return null; // let `required` handle missing values
    }

    const fromDateTime = DateTools.combineDateAndTimeLocal(from, fromTime);
    const toDateTime = DateTools.combineDateAndTimeLocal(to, toTime);

    if (!toDateTime.isAfter(fromDateTime)) {
      return { toBeforeFrom: true };
    }

    const rangeHours = toDateTime.diff(fromDateTime, 'hours', true);
    if (rangeHours > MAX_RANGE_HOURS) {
      return { rangeTooLong: true };
    }

    return null;
  };
}

@Component({
  selector: 'app-add-storm-dialog',
  imports: [
    MatDialogTitle, MatDialogContent, MatDialogActions, MatButton, MatDialogClose,
    MatFormField,
    MatLabel,
    MatInput,
    ReactiveFormsModule,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    MatButton,
    MatError,
    MatProgressSpinner,
  ],
  templateUrl: './add-storm-dialog.component.html',
  styleUrl: './add-storm-dialog.component.scss',
})
export class AddStormDialogComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  stormService = inject(StormService);
  fb = inject(FormBuilder);
  toast = inject(ToastService);
  stormForm: FormGroup = this.fb.group(
    {
      name: ['', Validators.required],
      from: [null as Moment | null, Validators.required],
      fromTime: ['00:00', Validators.required],
      to: [null as Moment | null, Validators.required],
      toTime: ['00:00', Validators.required],
    },
    { validators: dateRangeValidator() }
  );
  isCreating = false;

  constructor(
    public dialogRef: MatDialogRef<AddStormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: string
  ) {}

  ngOnInit(): void {
    merge(
      this.stormForm.get('from')!.valueChanges,
      this.stormForm.get('fromTime')!.valueChanges
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const fromValue: Moment | null = this.stormForm.get('from')!.value;
        const fromTime: string = this.stormForm.get('fromTime')!.value;
        if (!fromValue) {
          return;
        }
        const adjustedTo = fromValue.clone().add(1, 'day');
        this.stormForm.get('to')!.setValue(adjustedTo, { emitEvent: false });
        if (fromTime) {
          this.stormForm.get('toTime')!.setValue(fromTime, { emitEvent: false });
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get nameForm() {
    return this.stormForm.get('name')!;
  }

  get fromDateForm() {
    return this.stormForm.get('from')!;
  }

  get toDateForm() {
    return this.stormForm.get('to')!;
  }

  submitStormEvent(): void {
    if (this.stormForm.invalid) {
      this.stormForm.markAllAsTouched();
      return;
    }

    const payload = this.stormForm.value;
    const from = DateTools.combineDateAndTimeLocal(payload.from, payload.fromTime);
    const to = DateTools.combineDateAndTimeLocal(payload.to, payload.toTime);

    this.isCreating = true;
    this.stormService
      .createStorm(payload.name, from.toDate(), to.toDate())
      .then(response => {
        this.isCreating = false;
        this.toast.short(`Storm "${response.data.name}" created.`);
        this.stormForm.reset({ name: '', from: null, fromTime: '00:00', to: null, toTime: '00:00' });
        this.dialogRef.close(true);
      })
      .catch(error => {
        this.isCreating = false;
        this.toast.long('Error creating storm: ' + error);
      });
  }
}
